import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { DownloadIcon } from '@/atoms/Icons/DownloadIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { KeepsakeIcon } from '@/atoms/Icons/KeepsakeIcon'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { ShareIcon } from '@/atoms/Icons/ShareIcon'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD, CaptionSM, CaptionXS, HeadingXS, TitleLG, TitleMD, TitleSM, TitleXS } from '@/atoms/Text'
import {
  AvailableMoment,
  CollectibleAddToCartItem,
  CollectibleCartItem,
  PackCartProduct,
  useCollectiblesPurchaseContext,
} from '@/contexts/CollectiblesPurchaseContext/CollectiblesPurchaseContextProvider'
import { formatPrice } from '@/services/PriceFormatter'
import {
  CollectibleOffer,
  CollectibleProductMoment,
  CollectibleProductPack,
  CollectibleProductSignature,
  Maybe,
} from '@/types/codegen-federation'
import { CollectiblesGoalTrackerBreakdown } from '../CollectiblesGoalTrackerBreakdown'
import { CollectiblesPackItem } from './CollectiblesPackItem'
import { CollectiblesPackSoldOutItem } from './CollectiblesPackSoldOutItem'

interface CollectiblesPacksViewProps {
  offers: CollectibleOffer[]
  packOffers: CollectibleOffer[]
  keepsakeOffers: CollectibleOffer[]
  signatureOffers: CollectibleOffer[]
  momentOffers: CollectibleOffer[]
  projectId?: string
}

export const getPackSavings = (pack: CollectibleProductPack) => {
  const pricePer = (pack?.price ?? 0) / (pack?.quantity ?? 0)
  const savings = Math.floor(((4500 - pricePer) / 4500) * 100)

  return savings
}

const formatMomentDisplay = (p: CollectibleProductMoment) => {
  return `Season ${p.moment?.episode?.season?.seasonNumber} Episode ${p?.moment?.episode?.episodeNumber}`
}

const getPackSelection = (selectedOffer: CollectibleOffer | null) => {
  const selectedPack = selectedOffer?.products?.find(
    (product): product is CollectibleProductPack =>
      product?.__typename === 'CollectibleProductPack' && product?.soldOut === false,
  )
  return selectedPack || null
}

export const CollectiblesPacksView: React.FC<CollectiblesPacksViewProps> = ({
  packOffers,
  signatureOffers,
  keepsakeOffers,
  momentOffers,
}) => {
  const [selectedOffer, setSelectedOffer] = useState<CollectibleOffer | null>(packOffers?.[0] ?? null)
  const { cartItems, onAddItemsToCart, resetCartState } = useCollectiblesPurchaseContext()
  const [selectedFeature, setSelectedFeature] = useState<CollectibleProductMoment | null>()

  useEffect(() => {
    setSelectedOffer(packOffers?.[0] ?? null)
  }, [packOffers])

  useEffect(() => {
    const newSelectedPack = getPackSelection(selectedOffer)
    setSelectedPack(newSelectedPack)
  }, [selectedOffer, resetCartState])

  const [selectedPack, setSelectedPack] = useState<CollectibleProductPack | null>(getPackSelection(selectedOffer))

  const collectiblePacks = selectedOffer?.products?.filter(
    (product): product is CollectibleProductPack => product?.__typename === 'CollectibleProductPack',
  ) as Maybe<CollectibleProductPack>[]

  const handleSelectOffer = useCallback(
    (offer: CollectibleOffer | null) => {
      const pack = getPackSelection(offer)
      setSelectedOffer(offer)
      setSelectedPack(pack)
    },
    [setSelectedOffer, setSelectedPack],
  )

  const handleSelectPack = useCallback(
    (pack: CollectibleProductPack) => {
      setSelectedPack(pack)
    },
    [setSelectedPack],
  )

  const packInput = useMemo(
    () => ({
      offerId: selectedOffer?.id ?? '',
      productId: selectedPack?.id ?? '',
      name: selectedPack?.name ?? '',
      image: selectedPack?.image ?? '',
      price: selectedPack?.price ?? 0,
    }),
    [selectedOffer?.id, selectedPack],
  ) as PackCartProduct

  const offerInput = useMemo(
    () => ({
      offerId: selectedOffer?.id ?? '',
      total: selectedPack?.price ?? 0,
      products: [{ productId: selectedPack?.id, price: selectedPack?.price }],
    }),
    [selectedOffer, selectedPack],
  ) as CollectibleCartItem

  const featuredInput = useMemo(
    () => ({
      offerId: selectedFeature
        ? momentOffers.find((o) => o.products?.includes(selectedFeature))?.id
        : selectedOffer
        ? selectedOffer?.id
        : 0,
      total: selectedFeature?.price ?? 0,
      products: [{ productId: selectedFeature?.id, price: selectedFeature?.price }],
    }),
    [selectedOffer, selectedFeature, momentOffers],
  ) as CollectibleCartItem

  const handleAddToCart = useCallback(() => {
    const items: CollectibleAddToCartItem[] = []
    if (selectedPack) {
      const item: CollectibleAddToCartItem = {
        offerInput,
        packInput,
      }
      items.push(item)
    }
    if (selectedFeature) {
      const item: CollectibleAddToCartItem = {
        offerInput: featuredInput,
        momentInput: {
          ...selectedFeature?.moment,
          defaultOffer: { id: featuredInput.offerId, products: [{ id: selectedFeature.id }] },
        } as AvailableMoment,
      }
      items.push(item)
    }
    onAddItemsToCart(items)
  }, [offerInput, packInput, selectedPack, selectedFeature, featuredInput, onAddItemsToCart])

  const existingCartItem = useMemo(() => {
    const foundCartItem = cartItems.find((item) => item.offerId === selectedOffer?.id)

    if (foundCartItem && selectedPack) {
      // const matchedProduct = foundCartItem.products.find(
      //   (product) => (product as CollectibleProductInput).productId === selectedPack.id,
      // ) as CollectibleProductInput

      // return matchedProduct ? true : false
      return true
    }

    return false
  }, [cartItems, selectedOffer, selectedPack])

  const priceForSelections = useMemo(() => {
    let total: number = 0
    if (selectedPack) total = selectedPack?.price as number
    if (selectedFeature && selectedPack) total = (selectedPack?.price as number) + (selectedFeature?.price as number)
    return total
  }, [selectedPack, selectedFeature])

  const images = selectedPack?.images ?? (selectedPack ? [selectedPack?.image] : [])

  return (
    <div className="flex flex-wrap justify-center gap-8 py-5 lg:grid lg:grid-cols-[1fr_33%]">
      <div className="flex flex-col gap-4">
        {momentOffers?.length > 0 && (
          <div className="flex flex-row flex-wrap gap-4">
            <div className="place-self-center">
              <TitleMD weight="bold" className="pb-4 lg:hidden lg:pb-0">
                Choose a Variety Pack or a Featured Scene
              </TitleMD>
              <div
                className={classNames([
                  'rounded-lg border-2 p-10 cursor-pointer place-self-center w-fit',
                  !selectedFeature ? 'bg-oxide-primary/10 border-oxide-primary' : 'border-core-gray-300',
                ])}
                onClick={() => {
                  handleSelectOffer(packOffers?.[0] ?? null)
                  setSelectedFeature(undefined)
                }}
              >
                <CaptionSM>Variety Pack</CaptionSM>
              </div>
            </div>
            <div className="relative hidden w-[2px] border border-core-gray-300 bg-core-gray-300 py-10 lg:flex">
              &nbsp;
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  <TitleSM>Featured Scenes</TitleSM>
                  <TitleSM className="italic">(Limited Time Only)</TitleSM>
                </div>
                <CaptionSM>Guarantees the selected scene will be included in your pack purchase.</CaptionSM>
              </div>
              <div className="flex flex-wrap gap-4">
                {momentOffers?.map((momentOffer) => {
                  const p = momentOffer?.products?.find(
                    (p) => p?.__typename === 'CollectibleProductMoment',
                  ) as CollectibleProductMoment
                  return (
                    <div
                      key={p.id}
                      className={classNames([
                        'flex flex-col gap-1 rounded-lg border-2 p-4 cursor-pointer max-w-[200px]',
                        selectedFeature?.id === p?.id
                          ? 'bg-oxide-primary/10 border-oxide-primary'
                          : 'border-core-gray-300',
                      ])}
                      onClick={() => {
                        handleSelectOffer(momentOffer)
                        setSelectedFeature(p)
                      }}
                    >
                      <img src={p?.image as string} alt={p?.name as string} className="max-w-[120px]" />
                      <CaptionSM>{p?.moment?.title}</CaptionSM>
                      <CaptionXS className="-mt-1">{formatMomentDisplay(p)}</CaptionXS>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
        <div className="order-first h-fit w-full rounded-xl bg-core-gray-200 p-6 lg:order-last lg:p-10">
          {images && images.length > 0 ? (
            <>
              <FrameDisplay images={images as string[]} />
            </>
          ) : (
            <>
              {packOffers && packOffers.length > 0 && (
                <img
                  src={selectedPack ? selectedPack?.image ?? '' : selectedOffer?.image ?? ''}
                  alt="placeholder"
                  className="mx-auto h-full object-contain lg:object-cover"
                />
              )}
            </>
          )}
          {(!packOffers || packOffers?.length === 0) && (
            <img
              src="https://images.angelstudios.com/image/upload/v1756153598/angel-studios/seo/angel-opengraph-5.png"
              alt="placeholder"
              className="mx-auto h-full w-3/4 object-contain"
            />
          )}
        </div>
      </div>
      <div className="w-full lg:col-start-2 lg:row-span-12">
        <div className="my-2 flex gap-2">
          <SceneIcon color2="collectible-gold" />
          <CaptionSM color="collectible-gold">Golden Scenes</CaptionSM>
        </div>
        <HeadingXS weight="bold" className="my-4">
          Buy a Digital Collectible Pack
        </HeadingXS>
        {packOffers && packOffers?.length > 1 && (
          <CaptionLG className="pb-3">
            First select the season you want your scenes to be from, then select your pack size.
          </CaptionLG>
        )}
        <Image
          src="/v1717105529/collectibles/signature-series-2.gif"
          alt="Signature Series"
          width={200}
          height={100}
          className="my-1"
        />
        <CaptionLG color="core-gray-700" italic>
          Actor signatures upgrades are available after purchase.
        </CaptionLG>
        {selectedFeature && (
          <div className="my-12">
            <TitleMD weight="bold">1. Pick Your Pack</TitleMD>
            <div className="flex flex-wrap gap-3 pt-4">
              {momentOffers?.map((offer) => {
                const isSelected = selectedOffer?.id === offer?.id
                const isOfferSoldOut = offer.products?.every((p) => (p as CollectibleProductPack).soldOut)
                return (
                  <div
                    key={offer?.id}
                    className={classNames(
                      'flex rounded-lg border-2 p-4 cursor-pointer flex-wrap justify-center w-[47%] text-center space-y-2 h-full',
                      isSelected ? 'bg-oxide-primary/10 border-oxide-primary' : 'border-core-gray-300',
                    )}
                    onClick={() => handleSelectOffer(offer)}
                  >
                    {offer?.image && (
                      <div className="flex flex-wrap place-self-end">
                        <img src={offer?.image ?? ''} alt="placeholder" className="h-12 w-24" />
                      </div>
                    )}
                    <CaptionSM weight="bold" className="w-full">
                      {offer?.name?.replace('Digital Collectible Packs', '')}
                    </CaptionSM>
                    {isOfferSoldOut && (
                      <CaptionSM color="white" className="m-1 w-fit rounded-xl bg-error-700 px-2 py-1 font-thin">
                        Sold Out
                      </CaptionSM>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {packOffers && packOffers?.length > 0 && !selectedFeature && (
          <div className="my-12">
            <TitleMD weight="bold">Pick Your Pack</TitleMD>
            <div className="flex flex-wrap gap-3 pt-4">
              {packOffers?.map((offer) => {
                const isSelected = selectedOffer?.id === offer?.id
                const isOfferSoldOut = offer.products?.every((p) => (p as CollectibleProductPack).soldOut)
                return (
                  <div
                    key={offer?.id}
                    className={classNames(
                      'flex rounded-lg border-2 p-4 flex-wrap justify-center w-[47%] text-center space-y-2 h-full cursor-pointer',
                      isSelected ? 'bg-oxide-primary/10 border-oxide-primary' : 'border-core-gray-300',
                    )}
                    onClick={() => handleSelectOffer(offer)}
                  >
                    {offer?.image && (
                      <div className="flex flex-wrap place-self-end">
                        <img src={offer?.image ?? ''} alt="placeholder" className="h-12 w-24" />
                      </div>
                    )}
                    <CaptionSM weight="bold" className="w-full">
                      {offer?.name?.replace('Digital Collectible Packs', '')}
                    </CaptionSM>
                    {isOfferSoldOut && (
                      <CaptionSM color="white" className="m-1 w-fit rounded-xl bg-error-700 px-2 py-1 font-thin">
                        Sold Out
                      </CaptionSM>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {(!packOffers || packOffers?.length === 0) && (
          <div className="py-10">
            <TitleMD weight="bold">No offers are currently available. Check back soon!</TitleMD>
          </div>
        )}
        {packOffers && packOffers.length > 0 && (
          <div className="my-12">
            <TitleMD weight="bold">Pick Your Pack Size</TitleMD>
            {collectiblePacks?.map((pack) => {
              const isSelected = selectedPack?.id === pack?.id
              const isSoldOut = pack?.soldOut === true
              const savings = getPackSavings(pack as CollectibleProductPack)
              const fullPrice = (pack?.quantity ?? 0) * 4500

              return (
                <>
                  {!isSoldOut && (
                    <CollectiblesPackItem
                      pack={pack}
                      isSelected={isSelected}
                      handleSelectPack={handleSelectPack}
                      savings={savings}
                      fullPrice={fullPrice}
                    />
                  )}
                  {isSoldOut && <CollectiblesPackSoldOutItem pack={pack} fullPrice={fullPrice} savings={savings} />}
                </>
              )
            })}
          </div>
        )}
        {selectedFeature && (
          <div className="flex flex-col gap-2 pb-6">
            <TitleSM weight="bold">You&apos;ve chosen to guarantee a scene from:</TitleSM>
            <div
              className={classNames([
                'flex gap-x-2 rounded-lg border-2 bg-oxide-primary/10 border-oxide-primary p-4 w-full',
                'place-items-center justify-between cursor-pointer',
              ])}
              onClick={() => {
                setSelectedFeature(undefined)
                setSelectedOffer(packOffers?.[0] ?? null)
              }}
            >
              <div className="flex place-items-center gap-4">
                <CircleCheckIcon size={24} color1="oxide-primary" color2="white" />
                <div>
                  <img
                    src={selectedFeature?.image as string}
                    alt={selectedFeature?.name as string}
                    className="max-w-[120px]"
                  />
                  <CaptionMD weight="bold" className="pt-2">
                    {selectedFeature?.moment?.title}
                  </CaptionMD>
                  <CaptionSM>{formatMomentDisplay(selectedFeature)}</CaptionSM>
                </div>
              </div>
              <CaptionLG className="">
                {formatPrice(selectedFeature?.price ?? 0, { currency: 'USD', ignoreEmptyDecimal: true })}
              </CaptionLG>
            </div>
          </div>
        )}
        <div className="mt-14 rounded-xl bg-core-gray-100 px-4 py-6">
          {selectedPack && (
            <TitleLG weight="bold">
              {formatPrice(priceForSelections, { currency: 'USD', ignoreEmptyDecimal: true })}
            </TitleLG>
          )}
          <Button
            variant="oxide-primary"
            className="mt-4 w-full"
            onClick={handleAddToCart}
            disabled={!selectedPack || !!selectedPack?.soldOut}
          >
            {existingCartItem ? 'Continue' : 'Add to Cart'}
          </Button>
          {/* {existingCartItem && (
            <div className="mt-2 flex cursor-pointer items-center justify-center" onClick={resetCartState}>
              <TrashIcon size={24} color="black" />
              <CaptionSM>Clear cart</CaptionSM>
            </div>
          )} */}
        </div>
      </div>
      <div>
        <div className="mb-5 rounded-xl bg-core-gray-100 p-5">
          <CollectiblesGoalTrackerBreakdown slug={'wingfeather-saga'} />
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex w-full items-center gap-x-4 sm:w-[48%]">
            <DownloadIcon size={24} color="warm-03" />
            <CaptionLG className="w-[90%]">Download your frame to use as wallpaper, as a background, etc.</CaptionLG>
          </div>
          <div className="flex w-full items-center gap-x-4 sm:w-[48%]">
            <GiftIcon size={24} color="cool-02" />
            <CaptionLG className="w-[90%]">Dedicate or gift your Collectible to a loved one</CaptionLG>
          </div>
          <div className="flex w-full items-center gap-x-4 sm:w-[48%]">
            <KeepsakeIcon size={24} color="cool-01" />
            <CaptionLG className="w-[90%]">Order beautiful prints or print yourself</CaptionLG>
          </div>
          <div className="flex w-full items-center gap-x-4 sm:w-[48%]">
            <ShareIcon size={24} color="warm-01" />
            <CaptionLG className="w-[90%]">
              Easily share a link with others that shows exactly which part of the scene you own
            </CaptionLG>
          </div>
        </div>
      </div>
      {keepsakeOffers && keepsakeOffers.length > 0 && (
        <div className="pr-4">
          <TitleMD weight="bold" className="my-6">
            Frames from fans
          </TitleMD>
          <div className="grid grid-cols-3 gap-5">
            <div>
              <img
                src="https://images.angelstudios.com/image/upload/v1713892031/collectibles/wingfeather-saga/wingfeather-fan-3.png"
                alt="Wingfeather Saga Fan Picture"
                className="rounded-md"
              />
            </div>
            <div>
              <img
                src="https://images.angelstudios.com/image/upload/v1713892031/collectibles/wingfeather-saga/wingfeather-fan-4.png"
                alt="Wingfeather Saga Fan Picture"
                className="rounded-md"
              />
            </div>
            <div>
              <img
                src="https://images.angelstudios.com/image/upload/v1713892033/collectibles/wingfeather-saga/wingfeather-fan-1.png"
                alt="Wingfeather Saga Fan Picture"
                className="rounded-md"
              />
            </div>
            <div>
              <img
                src="https://images.angelstudios.com/image/upload/v1713892032/collectibles/wingfeather-saga/wingfeather-fan-2.png"
                alt="Wingfeather Saga Fan Picture"
                className="rounded-md"
              />
            </div>
          </div>
        </div>
      )}
      {signatureOffers && signatureOffers.length > 0 && (
        <div>
          <div className="align-center my-6 flex flex-row gap-2">
            <TitleMD weight="bold">Talent Signers.</TitleMD>
            <TitleMD className="italic">Upgrade available after purchase</TitleMD>
          </div>
          <div className="flex w-full flex-row flex-wrap justify-items-center gap-0">
            {signatureOffers?.[0]?.products?.map((s) => {
              const product = s as CollectibleProductSignature
              return (
                <div
                  key={product?.id}
                  className={classNames(
                    'flex flex-col py-4 gap-4 text-center flex-wrap w-1/2 lg:w-1/4 justify-items-center place-items-center',
                  )}
                >
                  <img src={product?.signerImage ?? ''} alt={product?.signerName ?? ''} className="w-3/4" />
                  <div className="space-y-3 text-center">
                    <TitleXS weight="bold">{product.signerName}</TitleXS>
                    <TitleXS>{product.signerRole}</TitleXS>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export const FrameDisplay = ({ images }: { images: string[] }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearCurrentInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
  }

  useEffect(() => {
    clearCurrentInterval()
    intervalRef.current = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)
    }, 3000)

    return () => {
      clearCurrentInterval()
    }
  }, [images.length])

  useEffect(() => {
    setCurrentImageIndex(0)
  }, [images])

  return (
    <div className="aspect-h-9 aspect-w-16">
      <img
        src={images[currentImageIndex]}
        alt="Example of available frame"
        className="mx-auto h-full object-contain lg:object-cover"
      />
    </div>
  )
}
